import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { apiKeysApi } from '@/services/apiKeysApi';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Key, Plus, Pencil, Eye, CheckCircle2, XCircle, Trash2, Copy, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { LoadingState } from '@/components/common';
import type { ApiKey, ApiKeyCreateRequest, ApiKeyUpdateRequest } from '@/types/apiKey';

export const ApiKeysListPage: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAdminAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [validateResult, setValidateResult] = useState<{ keyId: string; valid: boolean; message?: string } | null>(null);
  const [createdPlainKey, setCreatedPlainKey] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [expandedKeyId, setExpandedKeyId] = useState<string | null>(null);
  const [detailKey, setDetailKey] = useState<ApiKey | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  /** When true, the backend returned 404 — API Keys endpoint not implemented yet. */
  const [apiNotFound, setApiNotFound] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    setApiNotFound(false);
    try {
      const res = await apiKeysApi.list({ page: 0, size: 500 });
      setKeys(res.content ?? []);
    } catch (e) {
      const status = e && typeof e === 'object' && 'status' in e ? (e as { status: number }).status : undefined;
      if (status === 404) {
        setApiNotFound(true);
        setError(new Error('API Keys endpoint not available (404). The user-identity service may not implement this yet.'));
      } else {
        setError(e instanceof Error ? e : new Error('Failed to load API keys'));
      }
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    fetchKeys();
  }, [isSuperAdmin, navigate, fetchKeys]);

  const handleCreate = async (data: ApiKeyCreateRequest) => {
    setActionLoading('create');
    try {
      const created = await apiKeysApi.create(data);
      setCreatedPlainKey(created.plainKey ?? null);
      setCreateOpen(false);
      await fetchKeys();
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to create API key'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdate = async (id: string, data: ApiKeyUpdateRequest) => {
    setActionLoading(id);
    try {
      await apiKeysApi.update(id, data);
      setEditOpen(false);
      setSelectedKey(null);
      await fetchKeys();
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to update API key'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleValidate = async (id: string) => {
    setActionLoading(`validate-${id}`);
    try {
      const res = await apiKeysApi.validate(id);
      setValidateResult({ keyId: id, valid: res.valid, message: res.message });
    } catch (e) {
      setValidateResult({
        keyId: id,
        valid: false,
        message: e instanceof Error ? e.message : 'Validation request failed',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    if (!window.confirm(`Revoke API key "${key.name}"? This cannot be undone.`)) return;
    setActionLoading(key.id);
    try {
      await apiKeysApi.revoke(key.id);
      await fetchKeys();
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to revoke API key'));
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add toast: "Copied to clipboard"
  };

  if (!isSuperAdmin) return null;
  if (loading) return <LoadingState message="Loading API keys..." />;

  const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
  const isExpired = (k: ApiKey) => k.validUntil ? new Date(k.validUntil) < new Date() : false;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Key className="h-7 w-7 text-primary" />
              API Keys
            </h1>
            <span className="inline-flex items-center gap-1 rounded-md bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary border border-primary/30">
              <Shield className="h-3.5 w-3.5" /> Super admin only
            </span>
          </div>
          <p className="text-muted-foreground mt-1">
            View and manage API keys: create, edit expiry, validate, and revoke. Full key information is shown below.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2" disabled={!!actionLoading}>
          <Plus className="h-4 w-4" />
          Create API Key
        </Button>
      </div>

      {error && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-amber-700 dark:text-amber-400">
              {apiNotFound ? 'API Keys endpoint not available' : 'Error loading API keys'}
            </CardTitle>
            <CardDescription>
              {apiNotFound ? (
                <>
                  The server returned 404 for <code className="text-xs bg-muted px-1 rounded">/api/user-identity/api/v1/api-keys</code>.
                  Implement this endpoint on the <strong>user-identity</strong> service (e.g. GET list, POST create, PUT update, POST validate, DELETE revoke) to enable API key management.
                </>
              ) : (
                error.message
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={fetchKeys}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Keys</CardTitle>
          <CardDescription>List of API keys. Use Create to add a new key; store the secret once shown.</CardDescription>
        </CardHeader>
        <CardContent>
          {keys.length === 0 && !error ? (
            <div className="py-12 text-center text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No API keys yet. Create one to get started.</p>
              <Button className="mt-4" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create API Key
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-9" />
                  <TableHead>Name</TableHead>
                  <TableHead>Key (prefix)</TableHead>
                  <TableHead>Valid until / Expiry</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => {
                  const expired = isExpired(k);
                  return (
                    <React.Fragment key={k.id}>
                      <TableRow
                        className={expandedKeyId === k.id ? 'bg-muted/30' : ''}
                      >
                        <TableCell className="w-9">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setExpandedKeyId((id) => (id === k.id ? null : k.id))}
                            title={expandedKeyId === k.id ? 'Collapse details' : 'Show full details'}
                          >
                            {expandedKeyId === k.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{k.name}</p>
                            {k.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{k.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 font-mono text-sm">
                            <span>{k.keyPrefix ?? '—'}</span>
                            {k.keyPrefix && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={() => copyToClipboard(k.keyPrefix!)}
                                title="Copy prefix"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {k.validUntil ? (
                              <>
                                <span className={expired ? 'text-destructive font-medium' : ''}>
                                  {new Date(k.validUntil).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                                </span>
                                {expired && (
                                  <span className="block text-xs text-destructive">Expired</span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground">No expiry</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {k.lastUsedAt ? formatDate(k.lastUsedAt) : 'Never'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {k.createdAt ? formatDate(k.createdAt) : '—'}
                        </TableCell>
                        <TableCell>
                          {k.active ? (
                            expired ? (
                              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <XCircle className="h-4 w-4" /> Expired
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                <CheckCircle2 className="h-4 w-4" /> Active
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <XCircle className="h-4 w-4" /> Revoked
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDetailKey(k)}
                              disabled={!!actionLoading}
                              title="View full details"
                              aria-label="View full details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleValidate(k.id)}
                              disabled={!!actionLoading || !k.active}
                              title="Validate key"
                            >
                              {actionLoading === `validate-${k.id}` ? (
                                <BrandedSpinner />
                              ) : (
                                <CheckCircle2 className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedKey(k);
                                setEditOpen(true);
                              }}
                              disabled={!!actionLoading}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {k.active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevoke(k)}
                                disabled={!!actionLoading}
                                title="Revoke"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedKeyId === k.id && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-muted/20 p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                              <div><span className="text-muted-foreground">ID</span><p className="font-mono break-all mt-0.5">{k.id}</p></div>
                              <div><span className="text-muted-foreground">Name</span><p className="mt-0.5">{k.name}</p></div>
                              <div><span className="text-muted-foreground">Key prefix</span><p className="font-mono mt-0.5">{k.keyPrefix ?? '—'}</p></div>
                              <div><span className="text-muted-foreground">Description</span><p className="mt-0.5">{k.description || '—'}</p></div>
                              <div><span className="text-muted-foreground">Valid until</span><p className="mt-0.5">{k.validUntil ? formatDate(k.validUntil) : 'No expiry'}</p></div>
                              <div><span className="text-muted-foreground">Last used</span><p className="mt-0.5">{k.lastUsedAt ? formatDate(k.lastUsedAt) : 'Never'}</p></div>
                              <div><span className="text-muted-foreground">Active</span><p className="mt-0.5">{k.active ? 'Yes' : 'No (revoked)'}</p></div>
                              <div><span className="text-muted-foreground">Created</span><p className="mt-0.5">{k.createdAt ? formatDate(k.createdAt) : '—'}</p></div>
                              <div><span className="text-muted-foreground">Updated</span><p className="mt-0.5">{k.updatedAt ? formatDate(k.updatedAt) : '—'}</p></div>
                              <div><span className="text-muted-foreground">Created by</span><p className="mt-0.5">{k.createdBy ?? '—'}</p></div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <CreateKeyDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreatedPlainKey(null);
        }}
        onSubmit={handleCreate}
        loading={actionLoading === 'create'}
        createdPlainKey={createdPlainKey}
        onCopy={copyToClipboard}
      />

      {/* Edit dialog */}
      {selectedKey && (
        <EditKeyDialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
            setSelectedKey(null);
          }}
          keyEntity={selectedKey}
          onSubmit={(data) => handleUpdate(selectedKey.id, data)}
          loading={!!actionLoading}
        />
      )}

      {/* Full key details dialog */}
      <Dialog open={!!detailKey} onOpenChange={(open) => !open && setDetailKey(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>API key details</DialogTitle>
            <DialogDescription>Full information for this key. Visible to super admins only.</DialogDescription>
          </DialogHeader>
          {detailKey && (
            <div className="grid gap-4 py-2 text-sm">
              <div>
                <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">ID</span>
                <p className="font-mono break-all mt-1">{detailKey.id}</p>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Name</span>
                <p className="mt-1">{detailKey.name}</p>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Key prefix</span>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-mono">{detailKey.keyPrefix ?? '—'}</p>
                  {detailKey.keyPrefix && (
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(detailKey.keyPrefix!)}>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                    </Button>
                  )}
                </div>
              </div>
              {detailKey.description && (
                <div>
                  <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Description</span>
                  <p className="mt-1">{detailKey.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Valid until</span>
                  <p className="mt-1">{detailKey.validUntil ? formatDate(detailKey.validUntil) : 'No expiry'}</p>
                  {detailKey.validUntil && new Date(detailKey.validUntil) < new Date() && (
                    <p className="text-destructive text-xs mt-0.5">Expired</p>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Last used</span>
                  <p className="mt-1">{detailKey.lastUsedAt ? formatDate(detailKey.lastUsedAt) : 'Never'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Status</span>
                  <p className="mt-1">{detailKey.active ? 'Active' : 'Revoked'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Created</span>
                  <p className="mt-1">{detailKey.createdAt ? formatDate(detailKey.createdAt) : '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Updated</span>
                  <p className="mt-1">{detailKey.updatedAt ? formatDate(detailKey.updatedAt) : '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wider">Created by</span>
                  <p className="mt-1">{detailKey.createdBy ?? '—'}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailKey(null)}>Close</Button>
            {detailKey && (
              <Button onClick={() => { setSelectedKey(detailKey); setDetailKey(null); setEditOpen(true); }}>
                <Pencil className="h-4 w-4 mr-2" /> Edit key
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validate result dialog */}
      <Dialog open={!!validateResult} onOpenChange={(open) => !open && setValidateResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Validate result</DialogTitle>
            <DialogDescription>
              {validateResult?.valid ? (
                <span className="text-emerald-600 dark:text-emerald-400">Key is valid.</span>
              ) : (
                <span className="text-destructive">Key is invalid or expired.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {validateResult?.message && <p className="text-sm text-muted-foreground">{validateResult.message}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

function CreateKeyDialog({
  open,
  onClose,
  onSubmit,
  loading,
  createdPlainKey,
  onCopy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ApiKeyCreateRequest) => void;
  loading: boolean;
  createdPlainKey: string | null;
  onCopy: (t: string) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validUntil, setValidUntil] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      validUntil: validUntil.trim() || undefined,
    });
  };

  if (createdPlainKey) {
    return (
      <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API key created</DialogTitle>
            <DialogDescription>
              Copy the key now. You won&apos;t be able to see it again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={createdPlainKey} className="font-mono text-sm" />
            <Button variant="outline" size="icon" onClick={() => onCopy(createdPlainKey)} title="Copy">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>Add a new API key. You will see the secret once after creation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="create-name">Name *</Label>
              <Input
                id="create-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Production API"
                required
              />
            </div>
            <div>
              <Label htmlFor="create-desc">Description</Label>
              <Input
                id="create-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div>
              <Label htmlFor="create-valid">Valid until (date)</Label>
              <Input
                id="create-valid"
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading ? <BrandedSpinner /> : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditKeyDialog({
  open,
  onClose,
  keyEntity,
  onSubmit,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  keyEntity: ApiKey;
  onSubmit: (data: ApiKeyUpdateRequest) => void;
  loading: boolean;
}) {
  const [name, setName] = useState(keyEntity.name);
  const [description, setDescription] = useState(keyEntity.description ?? '');
  const [validUntil, setValidUntil] = useState(
    keyEntity.validUntil ? keyEntity.validUntil.slice(0, 10) : ''
  );
  const [active, setActive] = useState(keyEntity.active);

  React.useEffect(() => {
    setName(keyEntity.name);
    setDescription(keyEntity.description ?? '');
    setValidUntil(keyEntity.validUntil ? keyEntity.validUntil.slice(0, 10) : '');
    setActive(keyEntity.active);
  }, [keyEntity]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      validUntil: validUntil.trim() || undefined,
      active,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit API key</DialogTitle>
            <DialogDescription>Update name, description, validity, or active status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-desc">Description</Label>
              <Input
                id="edit-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="edit-valid">Valid until (date)</Label>
              <Input
                id="edit-valid"
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-active"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-border"
              />
              <Label htmlFor="edit-active">Active (key can be used)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? <BrandedSpinner /> : 'Update'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ApiKeysListPage;
